import { useState, useEffect, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import { GridColDef, GridSortModel, GridCellParams, GridRowSelectionModel } from '@mui/x-data-grid';
import { DataGrid, DataGridModel } from 'src/shared/components/organisms/dataGrid';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useTranslation } from 'react-i18next';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { useScrollIntoView } from 'src/shared/hooks/useScrollIntoView';
import { GetMemberInvoiceSelection } from 'src/shared/services/invoiceService';
import { extendObjectWithId } from 'src/shared/functions/extendObjectWithId';
import { AmountWithCurrency } from 'src/shared/components/atoms/amountWithCurrency';
import { useInvoiceContext } from 'src/shared/contexts/invoiceContext';
import { MemberInvoiceItem } from '../models/memberInvoiceItem';
import { MemberInvoiceSelectionSearchFilter } from '../models/memberInvoiceSelectionSearchFilter';
import { MemberInvoiceSelectionFamilyTooltip } from './memberInvoiceSelectionFamilyTooltip';
import { InvoiceCountStats } from '../models/invoiceCountStats';

type MemberInvoiceSelectionDataGridProps = {
  searchFilter: MemberInvoiceSelectionSearchFilter;
  searchFilterCallback: (filter: MemberInvoiceSelectionSearchFilter) => void;
};

const MemberInvoiceSelectionDataGrid = (props: MemberInvoiceSelectionDataGridProps) => {
  const { searchFilter, searchFilterCallback } = props;
  const { t } = useTranslation(['invoice']);
  const isMounted = useIsMounted();
  const { scrollIntoView, scrollRef } = useScrollIntoView();
  const { invoiceCountStats, setInvoiceCountStats } = useInvoiceContext();
  const { watch } = useFormContext();

  const [rows, setRows] = useState<MemberInvoiceItem[]>([]);
  const [data, setData] = useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: [5, 10, 15, 25, 50],
    pageSize: 25,
    page: 0,
  });
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'memberNr', sort: 'asc' }]);

  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  useEffect(() => {
    setData((prev) => ({ ...prev, ...{ page: searchFilter.pageIndex, pageSize: searchFilter.pageSize } }));
  }, [searchFilter]);

  const onPageChange = (newPageIndex: number) => {
    // updateData('page', newPageIndex);
    scrollIntoView();
    searchFilterCallback({ ...searchFilter, pageIndex: newPageIndex });
  };

  const onPageSizeChange = (newPageSize: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: 0, pageSize: newPageSize });
    scrollIntoView();
  };

  const fetchData = useCallback(async () => {
    updateData('loading', true);
    try {
      const result = await GetMemberInvoiceSelection(searchFilter);
      const invoiceMemberSelectionResult = extendObjectWithId(result.data.results, 'memberId');
      if (isMounted()) {
        updateData('totalRows', result.data.totalItems);
        const count = {
          emailCount: result.data.data.emailCount,
          mobilePhoneCount: result.data.data.mobilePhoneCount,
          totalCount: result.data.data.totalCount,
          creditRecipientCount: result.data.data.creditRecipientCount,
          creditTotalAmount: result.data.data.creditTotalAmount,
          creditZeroInvoiceCount: result.data.data.creditZeroInvoiceCount,
        } as InvoiceCountStats;
        setInvoiceCountStats(count);
        setRows(invoiceMemberSelectionResult);
      }
    } catch (e) {
      if (isMounted()) ConsoleHelper.log(e);
    } finally {
      if (isMounted()) updateData('loading', false);
    }
  }, [searchFilter, isMounted, setInvoiceCountStats]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const columns: GridColDef[] = [
    {
      field: 'memberNr',
      headerName: t('invoice:createMemberInvoice.tableHeaders.memberNr'),
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.memberNr}</>;
      },
    },
    {
      field: 'firstName',
      headerName: t('invoice:createMemberInvoice.tableHeaders.firstName'),
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.firstName}</>;
      },
    },
    {
      field: 'lastName',
      headerName: t('invoice:createMemberInvoice.tableHeaders.lastName'),
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.lastName}</>;
      },
    },
    {
      // Fakturan ställs på huvudmedlemmen när raden gäller en hushållsmedlem vars huvudmedlem
      // inte är med i urvalet. Då är det inte medlemmen på raden som får fakturan, och det ska synas.
      field: 'recipientName',
      headerName: t('invoice:createMemberInvoice.tableHeaders.recipient'),
      flex: 1,
      sortable: false,
      renderCell: (params: GridCellParams) => {
        if (!params.row.recipientName) return <></>;

        return (
          <Tooltip title={t('invoice:createMemberInvoice.tableHeaders.recipientHelp')} arrow>
            <Typography variant='body2'>{`${params.row.recipientName} (${params.row.recipientMemberNr})`}</Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'familyMembers',
      headerName: t('invoice:createMemberInvoice.tableHeaders.familyMembers'),
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return (
          <>
            <MemberInvoiceSelectionFamilyTooltip familyMembers={params.row.familyMembers} />
          </>
        );
      },
    },
    {
      field: 'fee',
      type: 'number',
      headerName: t('invoice:createMemberInvoice.tableHeaders.fee'),
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return <AmountWithCurrency amount={params.row.fee} />;
      },
    },
  ];

  // Kolumnerna visas först när kryssrutan är ibockad, alltså när avdraget faktiskt kommer
  // att göras. Annars skulle listan lova ett avdrag som körningen inte gör. Att det finns
  // tillgodo i urvalet syns ändå, i sammanfattningen ovanför listan.
  if (watch('useCredits') && (invoiceCountStats?.creditTotalAmount ?? 0) > 0) {
    columns.push({
      field: 'creditAmount',
      type: 'number',
      headerName: t('invoice:createMemberInvoice.tableHeaders.credit'),
      flex: 1,
      sortable: false,
      renderCell: (params: GridCellParams) => {
        if (!params.row.creditAmount) return <></>;

        return (
          <Tooltip title={t('invoice:createMemberInvoice.tableHeaders.creditHelp', { amount: params.row.creditAvailable })} arrow>
            <Box component='span'>
              <AmountWithCurrency amount={-params.row.creditAmount} />
            </Box>
          </Tooltip>
        );
      },
    });

    columns.push({
      field: 'amountAfterCredit',
      type: 'number',
      headerName: t('invoice:createMemberInvoice.tableHeaders.amountAfterCredit'),
      flex: 1,
      sortable: false,
      renderCell: (params: GridCellParams) => {
        // En faktura på noll kronor skapas ändå, men markeras som betald av körningen så att
        // medlemskapet förlängs. Det ska synas här, inte överraska efteråt.
        if (params.row.creditAmount > 0 && params.row.amountAfterCredit === 0) {
          return (
            <Tooltip title={t('invoice:createMemberInvoice.tableHeaders.zeroInvoiceHelp')} arrow>
              <Chip size='small' color='success' variant='outlined' label={t('invoice:createMemberInvoice.tableHeaders.zeroInvoice')} />
            </Tooltip>
          );
        }

        return <AmountWithCurrency amount={params.row.amountAfterCredit} />;
      },
    });
  }

  return (
    <Box
      sx={{
        position: 'relative',
        '& .MuiPaper-root': {
          boxShadow: 'none',
        },
      }}
      ref={scrollRef}
    >
      <DataGrid<MemberInvoiceItem>
        autoHeight
        columns={columns}
        rows={rows}
        dataModel={data}
        checkboxSelection={false}
        mode='server'
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        onSelectionModelChange={(newSelectionModel) => {
          setSelectionModel(newSelectionModel);
        }}
        selectionModel={selectionModel}
        sortModel={sortModel}
        onSortModelChange={(newSortModel) => {
          const sort = sortModel[0].sort === 'asc' ? 'desc' : 'asc';
          if (newSortModel.length) {
            setSortModel(newSortModel);
          } else {
            setSortModel([{ field: sortModel[0].field, sort }]);
          }
          searchFilterCallback({
            ...searchFilter,
            pageIndex: 0,
            sortCol: newSortModel.length ? newSortModel[0].field : sortModel[0].field,
            sortDir: newSortModel.length ? newSortModel[0].sort : sort,
          });
        }}
      />
    </Box>
  );
};

export default MemberInvoiceSelectionDataGrid;
